<script setup>
import { ref, watch, computed, onMounted, onUnmounted, defineEmits, defineProps } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()


const emit = defineEmits(['update:modelValue'])
const props = defineProps({
  modelValue: String,
  products: {
    type: Array,
    default: () => []
  }
})

const searchQuery = ref(props.modelValue)

watch(searchQuery, (val) => {
  emit('update:modelValue', val)
  if (val && val.trim().length > 0) {
    showSuggestions.value = true
  } else {
    showSuggestions.value = false
  }
})

const showSuggestions = ref(false)

const filteredSuggestions = computed(() => {
  if (!searchQuery.value || !props.products) return []
  const query = searchQuery.value.toLowerCase().trim()
  if (query === '') return []

  return props.products
    .filter(product => product.name && product.name.toLowerCase().includes(query))
    .slice(0, 10) // top 10 results
})

const goToProduct = (id) => {
  showSuggestions.value = false
  searchQuery.value = ''
  router.push(`/produits/${id}`)
}

const searchContainerRef = ref(null)

const handleClickOutside = (event) => {
  if (searchContainerRef.value && !searchContainerRef.value.contains(event.target)) {
    showSuggestions.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="search-container" ref="searchContainerRef">
    <div class="search-wrapper">
      <input
        class="search-input"
        type="search"
        v-model="searchQuery"
        :placeholder="$t('search') || 'Search...'"
      />
      <div class="search-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
      </div>

      <!-- Floating Suggestions Box -->
      <div v-if="showSuggestions && filteredSuggestions.length > 0" class="suggestions-box">
        <div
          v-for="product in filteredSuggestions"
          :key="product.id"
          class="suggestion-item"
          @click="goToProduct(product.id)"
        >
          <img :src="product.image || product.imageUrls" alt="" class="suggestion-img" loading="lazy" v-if="product.image || product.imageUrls" />
          <div class="suggestion-info">
            <div class="suggestion-name">{{ product.name }}</div>
            <div class="suggestion-price" v-if="product.models && product.models[0] && product.models[0].sell">{{ product.models[0].sell }} DA</div>
          </div>
        </div>
      </div>
      <div v-else-if="showSuggestions && searchQuery && searchQuery.trim().length > 0 && props.products.length > 0" class="suggestions-box empty-suggestions">
        {{ $t('noProducts') || 'No products found' }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.search-container {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.search-wrapper {
  position: relative;
  width: 100%;
  max-width: 600px; /* Constrain width for desktop */
}

.search-input {
  width: 100%;
  padding: 12px 45px 12px 20px;
  border: 1px solid var(--border, #e5e7eb);
  border-radius: var(--radius);
  background-color: var(--surface, #f8fafc);
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0,0,0,0.02);
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: var(--dark, #1e293b);
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 15px; /* Physical right */
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
  display: flex;
  align-items: center;
}

.search-icon svg {
  width: 20px;
  height: 20px;
}

/* RTL Support */
html[dir="rtl"] .search-icon {
  right: auto;
  left: 15px;
}

html[dir="rtl"] .search-input {
  padding: 12px 20px 12px 45px;
}


.suggestions-box {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 8px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  border: 1px solid #e2e8f0;
  z-index: 50;
  max-height: 350px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.suggestion-item {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s;
  text-align: left;
}

html[dir="rtl"] .suggestion-item {
  text-align: right;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover {
  background-color: #f8fafc;
}

.suggestion-img {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 15px;
}

html[dir="rtl"] .suggestion-img {
  margin-right: 0;
  margin-left: 15px;
}

.suggestion-info {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.suggestion-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.suggestion-price {
  color: var(--primary, #3b82f6);
  font-weight: 700;
  font-size: 0.9rem;
}

.empty-suggestions {
  padding: 20px;
  text-align: center;
  color: #64748b;
  font-size: 0.95rem;
}
</style>
